/**
 * Stage 5E.2 Phase 3 — shared BrandProvider.
 *
 * Consumes ONE upstream Stage 5D / Stage 5E.2 brand payload
 * (Company or Accountant) and exposes a normalised React context
 * via ``useBrand()``. The normaliser owns both the §8.1 envelope
 * and the Stage 5D legacy shape — pages never touch raw payloads.
 *
 * The provider DOES NOT fetch anything itself; it consumes the
 * payload from its upstream provider (CompanyBrandProvider /
 * AccountantBrandProvider). Phase 3 ships the layer; Phases 5–6
 * may later migrate the fetch responsibility into this provider.
 *
 * Lifecycle:
 *   * ``resolved=false`` until the upstream provider has finished
 *     its initial fetch (loading=false on the upstream).
 *   * On every payload update, the normaliser runs and CSS tokens
 *     are applied (or cleared, on standard_ordly / no payload).
 *   * On unmount, tokens are cleared synchronously.
 *
 * No-flash rule (contract §8): the provider exposes ``resolved`` so
 * the FirmFirstShell can hold the neutral skeleton until brand
 * resolution completes. Elapsed time NEVER triggers a fallback to
 * Ordly — only a confirmed resolver failure (``error===true``) does.
 */
import React, {
    createContext, useContext, useLayoutEffect, useMemo,
} from "react";
import { normalizeBrandPayload, ordlyNormalised } from "./normalize";
import { applyBrandTokens, clearBrandTokens } from "./tokens";

const BrandCtx = createContext(null);

export function BrandProvider(props) {
    // Upstream payload + status (Company or Accountant).
    const { payload, loading, error, shellFlagOn, children } = props;

    // === Scoped vs global mode discrimination ===
    //
    // GLOBAL MODE — ``scopedTarget`` prop NOT supplied. The provider
    //   writes branding to ``document.documentElement`` (tokens) and
    //   ``document.body`` (mode class). Normal Company / Accountant
    //   runtime (Phase 3 unchanged).
    //
    // SCOPED MODE — ``scopedTarget`` prop supplied (even when its
    //   value is ``null``). The provider writes branding ONLY to the
    //   resolved Element value of ``scopedTarget``. ``document.*`` is
    //   NEVER a fallback target — when ``scopedTarget`` is currently
    //   ``null`` (e.g. preview element not yet committed, detached,
    //   or being replaced), the provider applies branding NOWHERE.
    //
    // The two states "no scoped target requested" and "scoped target
    // requested but not currently available" MUST stay distinct;
    // collapsing them via ``ref?.current ?? null`` would silently
    // brand the Admin document during target absence. We discriminate
    // by checking whether the prop key was supplied at all on the
    // props object — destructuring with a default value would lose
    // this information.
    const isScoped = Object.prototype.hasOwnProperty.call(
        props, "scopedTarget",
    );
    // The resolved scoped element (Element | null). When ``isScoped``
    // is false this value is irrelevant.
    const scopedTarget = isScoped ? (props.scopedTarget || null) : null;

    // Normalise once per payload identity change.
    const normalised = useMemo(() => {
        if (loading) return ordlyNormalised();          // skeleton phase
        if (error) return ordlyNormalised();             // confirmed failure
        return normalizeBrandPayload(payload);
    }, [payload, loading, error]);

    // Apply tokens AND own cleanup in the same layout-effect lifecycle
    // (contract §15.2 — tokens/class must be applied or cleared
    // synchronously after the DOM commit and BEFORE the browser
    // paints; the cleanup branch runs on every effect re-entry and
    // on unmount/provider replacement under the same pre-paint
    // guarantee, so a stale brand can never paint).
    //
    // Target availability is REACTIVE: ``scopedTarget`` is a normal
    // React prop (the consumer wires it through a callback-ref +
    // useState pair), so detachment, replacement, and re-attachment
    // each trigger a commit → this effect re-runs with the new value.
    // We never depend on ref mutation alone.
    useLayoutEffect(() => {
        if (isScoped) {
            // SCOPED MODE
            if (!scopedTarget) {
                // Scoped target requested but not currently
                // available → NO-OP. Globals MUST NOT be touched.
                // Prior commits' cleanup already cleared whichever
                // element they had previously branded.
                return undefined;
            }
            // Capture the concrete element for THIS commit; cleanup
            // uses the captured value, never whatever the prop
            // happens to be on a later re-render.
            const captured = scopedTarget;
            if (normalised.mode === "firm_first") {
                applyBrandTokens(normalised.tokens, captured);
            } else {
                clearBrandTokens(captured);
            }
            return () => {
                clearBrandTokens(captured);
            };
        }
        // GLOBAL MODE (Phase 3 unchanged)
        if (normalised.mode === "firm_first") {
            applyBrandTokens(normalised.tokens);
        } else {
            clearBrandTokens();
        }
        return () => {
            clearBrandTokens();
        };
    }, [normalised, isScoped, scopedTarget]);

    const value = useMemo(() => ({
        // Resolution status (no-flash contract §8).
        // ``resolved`` is TRUE when:
        //   * upstream initial fetch has finished (loading=false), OR
        //   * upstream confirmed resolver failure (error=true).
        // It is FALSE only while the authoritative payload is still
        // pending — that is the only state in which consumers should
        // render the neutral skeleton.
        resolved: !loading,
        error: !!error,
        // Build-time flag passthrough (rarely needed by pages).
        shellFlagOn: shellFlagOn !== undefined ? !!shellFlagOn : true,
        // Normalised brand surface (the only thing pages should read).
        mode:            normalised.mode,
        displayName:     normalised.displayName,
        contactEmail:    normalised.contactEmail,
        contactPhone:    normalised.contactPhone,
        website:         normalised.website,
        logos:           normalised.logos,
        tokens:          normalised.tokens,
        attribution:     normalised.attribution,
    }), [normalised, loading, error, shellFlagOn]);

    return (
        <BrandCtx.Provider value={value}>
            {children}
        </BrandCtx.Provider>
    );
}

/**
 * Safe consumer hook. Outside any BrandProvider, falls back to
 * the standard_ordly resolved state — never throws.
 */
export function useBrand() {
    const v = useContext(BrandCtx);
    if (!v) {
        return {
            resolved: true,
            error: false,
            shellFlagOn: false,
            ...ordlyNormalised(),
        };
    }
    return v;
}
